/**
 * mSTEM Kit Manager Modal (GEN-04). Presentational component — Redux
 * wiring lives in src/containers/kit-manager-modal.jsx.
 *
 * Render branches (checked in order):
 *   - connectingKit → DeviceConnectionModal (GEN-04 S-3)
 *   - detailKit     → KitDetailModal (SMH-04, "Thông tin chi tiết kit")
 *   - inEditMode (isCreating OR editingKit) → KitEditForm + form title
 *   - else → toolbar (+ Add Kit button) + MstemTable of 4 mock kits
 *
 * RFID badge variant mapping: hasRFID=true→success ("Có"),
 * hasRFID=false→primary ("Không").
 *
 * Mirrors RobotManagerModalComponent from Plan 02-03 with kit fields per
 * DG-13/DG-14 (sensorCount / relayCount / hasRFID).
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';
import KitActionsCell from './kit-actions-cell.jsx';
import KitEditForm from './kit-edit-form.jsx';
import KitDetailModal from './kit-detail-modal.jsx';
import DeviceConnectionModal from '../../containers/device-connection-modal.jsx';

import styles from './kit-manager-modal.css';

const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Quản lý kit SmartHome',
        description: 'Kit manager modal title',
        id: 'gen.kit.modal.title'
    },
    colImage: {
        defaultMessage: 'Ảnh',
        description: 'Kit table column — image',
        id: 'gen.kit.table.col.image'
    },
    colName: {
        defaultMessage: 'Tên',
        description: 'Kit table column — name',
        id: 'gen.kit.table.col.name'
    },
    colSensors: {
        defaultMessage: 'Số sensor',
        description: 'Kit table column — sensors',
        id: 'gen.kit.table.col.sensors'
    },
    colRelays: {
        defaultMessage: 'Số relay',
        description: 'Kit table column — relays',
        id: 'gen.kit.table.col.relays'
    },
    colRFID: {
        defaultMessage: 'RFID',
        description: 'Kit table column — RFID',
        id: 'gen.kit.table.col.rfid'
    },
    colFirmware: {
        defaultMessage: 'Firmware',
        description: 'Kit table column — installed firmware version (SMH-04)',
        id: 'gen.kit.table.col.firmware'
    },
    colActions: {
        defaultMessage: 'Thao tác',
        description: 'Kit table column — actions',
        id: 'gen.kit.table.col.actions'
    },
    addKit: {
        defaultMessage: '+ Thêm kit',
        description: 'Add kit button',
        id: 'gen.kit.action.add'
    },
    edit: {
        defaultMessage: 'Sửa',
        description: 'Edit row tooltip',
        id: 'gen.kit.action.edit'
    },
    del: {
        defaultMessage: 'Xoá',
        description: 'Delete row tooltip',
        id: 'gen.kit.action.delete'
    },
    empty: {
        defaultMessage: 'Chưa có kit nào — bấm "+ Thêm kit" để bắt đầu',
        description: 'Empty state',
        id: 'gen.kit.empty'
    },
    rfidYes: {
        defaultMessage: 'Có',
        description: 'RFID present badge',
        id: 'gen.kit.badge.rfid.yes'
    },
    rfidNo: {
        defaultMessage: 'Không',
        description: 'RFID absent badge',
        id: 'gen.kit.badge.rfid.no'
    },
    editingTitle: {
        defaultMessage: 'Chỉnh sửa kit',
        description: 'Edit form title',
        id: 'gen.kit.form.title.edit'
    },
    creatingTitle: {
        defaultMessage: 'Thêm kit mới',
        description: 'Add form title',
        id: 'gen.kit.form.title.create'
    },
    searchPlaceholder: {
        defaultMessage: 'Tìm kiếm kit theo tên...',
        description: 'Kit search box placeholder (UC-04 S-2)',
        id: 'gen.kit.search.placeholder'
    },
    searchLabel: {
        defaultMessage: 'Tìm kiếm kit SmartHome',
        description: 'Kit search box aria-label',
        id: 'gen.kit.search.label'
    },
    searchEmpty: {
        defaultMessage: 'Không tìm thấy kit SmartHome phù hợp',
        description: 'Empty state when search yields no kits (UC-04 S-2)',
        id: 'gen.kit.search.empty'
    },
    connect: {
        defaultMessage: 'Kết nối',
        description: 'Connect row action (UC-04 S-3)',
        id: 'gen.kit.action.connect'
    },
    detail: {
        defaultMessage: 'Xem thông tin chi tiết & firmware',
        description: 'Detail row action — opens KitDetailModal (SMH-04)',
        id: 'gen.kit.action.detail'
    },
    fwUnknown: {
        defaultMessage: 'Chưa rõ',
        description: 'Kit table firmware cell when the version is unknown (SMH-04)',
        id: 'gen.kit.table.firmware.unknown'
    }
});

class KitManagerModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'renderImageCell',
            'renderRFIDCell',
            'renderFirmwareCell',
            'renderActionsCell'
        ]);
    }
    renderImageCell (row) {
        return (
            <div
                className={styles.avatarCell}
                title={row.brand}
            >
                {row.name.substring(0, 2).toUpperCase()}
            </div>
        );
    }
    renderRFIDCell (row) {
        const {intl} = this.props;
        return (
            <MstemBadge variant={row.hasRFID ? 'success' : 'primary'}>
                {row.hasRFID ?
                    intl.formatMessage(messages.rfidYes) :
                    intl.formatMessage(messages.rfidNo)}
            </MstemBadge>
        );
    }
    renderFirmwareCell (row) {
        // SMH-04: kits seeded before the detail block existed (or restored from
        // an older localStorage entry) have no version — show "Chưa rõ" rather
        // than an empty cell.
        const {intl} = this.props;
        return row.firmwareVersion ?
            row.firmwareVersion :
            intl.formatMessage(messages.fwUnknown);
    }
    renderActionsCell (row) {
        const {intl} = this.props;
        return (
            <KitActionsCell
                connectLabel={intl.formatMessage(messages.connect)}
                deleteLabel={intl.formatMessage(messages.del)}
                detailLabel={intl.formatMessage(messages.detail)}
                editLabel={intl.formatMessage(messages.edit)}
                row={row}
                onConnect={this.props.onClickConnect}
                onDelete={this.props.onClickDelete}
                onDetail={this.props.onClickDetail}
                onEdit={this.props.onClickEdit}
            />
        );
    }
    buildColumns () {
        const {intl} = this.props;
        return [
            {
                key: 'image',
                label: intl.formatMessage(messages.colImage),
                width: '64px',
                render: this.renderImageCell
            },
            {key: 'name', label: intl.formatMessage(messages.colName)},
            {key: 'sensorCount', label: intl.formatMessage(messages.colSensors), width: '120px'},
            {key: 'relayCount', label: intl.formatMessage(messages.colRelays), width: '120px'},
            {
                key: 'hasRFID',
                label: intl.formatMessage(messages.colRFID),
                width: '100px',
                render: this.renderRFIDCell
            },
            {
                key: 'firmwareVersion',
                label: intl.formatMessage(messages.colFirmware),
                width: '120px',
                render: this.renderFirmwareCell
            },
            {
                key: 'actions',
                label: intl.formatMessage(messages.colActions),
                width: '150px',
                render: this.renderActionsCell
            }
        ];
    }
    render () {
        const {
            kits,
            connectingKit,
            detailKit,
            detailFirmwares,
            editingKit,
            isCreating,
            intl,
            searchTerm,
            onCancelForm,
            onClickAdd,
            onCloseConnect,
            onCloseDetail,
            onRequestClose,
            onSearch,
            onSubmitForm
        } = this.props;
        const inEditMode = isCreating || !!editingKit;

        // UC-04 S-2: filter the list by name, case-insensitive.
        const term = (searchTerm || '').trim().toLowerCase();
        const filtered = term ?
            kits.filter(k => k.name.toLowerCase().includes(term)) :
            kits;
        const emptyMessage = term ?
            intl.formatMessage(messages.searchEmpty) :
            intl.formatMessage(messages.empty);

        if (connectingKit) {
            return (
                <DeviceConnectionModal
                    deviceName={connectingKit.name}
                    deviceType="kit"
                    onClose={onCloseConnect}
                />
            );
        }

        // SMH-04: the detail view replaces the manager (same pattern as the
        // connection wizard above) so only one modal is ever mounted.
        if (detailKit) {
            return (
                <KitDetailModal
                    firmwares={detailFirmwares}
                    kit={detailKit}
                    onRequestClose={onCloseDetail}
                />
            );
        }

        return (
            <Modal
                className={styles.modalContent}
                contentLabel={intl.formatMessage(messages.modalTitle)}
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    {inEditMode ? (
                        <div>
                            <h3 className={styles.formTitle}>
                                {isCreating ?
                                    <FormattedMessage {...messages.creatingTitle} /> :
                                    <FormattedMessage {...messages.editingTitle} />}
                            </h3>
                            <KitEditForm
                                initialKit={editingKit}
                                onCancel={onCancelForm}
                                onSave={onSubmitForm}
                            />
                        </div>
                    ) : (
                        <div>
                            <div className={styles.toolbar}>
                                <input
                                    aria-label={intl.formatMessage(messages.searchLabel)}
                                    className={styles.searchInput}
                                    placeholder={intl.formatMessage(messages.searchPlaceholder)}
                                    type="search"
                                    value={searchTerm}
                                    onChange={onSearch}
                                />
                                <button
                                    className={styles.btnPrimary}
                                    type="button"
                                    onClick={onClickAdd}
                                >
                                    <FormattedMessage {...messages.addKit} />
                                </button>
                            </div>
                            <MstemTable
                                columns={this.buildColumns()}
                                data={filtered}
                                emptyMessage={emptyMessage}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        );
    }
}

KitManagerModalComponent.propTypes = {
    connectingKit: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
    detailFirmwares: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
    detailKit: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
    editingKit: PropTypes.shape({id: PropTypes.string}),
    intl: intlShape.isRequired,
    isCreating: PropTypes.bool,
    kits: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    onCancelForm: PropTypes.func.isRequired,
    onClickAdd: PropTypes.func.isRequired,
    onClickConnect: PropTypes.func.isRequired,
    onClickDelete: PropTypes.func.isRequired,
    onClickDetail: PropTypes.func.isRequired,
    onClickEdit: PropTypes.func.isRequired,
    onCloseConnect: PropTypes.func.isRequired,
    onCloseDetail: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onSubmitForm: PropTypes.func.isRequired,
    searchTerm: PropTypes.string
};

export default injectIntl(KitManagerModalComponent);
